import { t } from "i18next";
import React, { useEffect, useState } from "react";
import CoreInputField from "../../../app/core/components/CoreInputField/CoreInputField";
import CoreButton from "../../../app/core/components/CoreButton/CoreButton";
import {
  Button,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  useCreateRoleMutation,
  useGetCapabilityQuery,
  useGetRoleByIdQuery,
  useUpdateRoleMutation,
  useAssignCapabilityBulkMutation,
} from "../../api/admin/rolesApi";
import { toast } from "react-toastify";
import {
  CAPABILITIES_ROLE,
  PRESET_ADMIN,
  PRESET_ADMINGOUP,
  PRESET_OPERATOR,
  PRESET_STUDIO,
} from "../../../utils/constants/capabilities";
import CoreCapabilities from "../../../app/core/components/CoreCapabilities/CoreCapabilities";

const DetailRuoli = ({ roleId, onClose }) => {
  const [capability, setCapability] = useState();
  const [selectedCap, setSelectedCap] = useState();
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    enabled: false,
    capabilities: [],
  });

  const adminRoleIds = CAPABILITIES_ROLE.map((role) => role.id);
  const presetAdmin = PRESET_ADMIN.map((cap) => cap.id);
  const presetStudio = PRESET_STUDIO.map((cap) => cap.id);
  const presetOperator = PRESET_OPERATOR.map((cap) => cap.id);
  const presetAdminGroup = PRESET_ADMINGOUP.map((cap) => cap.id);

  const { data: dataIdRole } = useGetRoleByIdQuery(roleId, { skip: !roleId });
  const { data: getCapability } = useGetCapabilityQuery();
  const [createRole, { isLoading: loadingCreate }] = useCreateRoleMutation();
  const [editRole, { isLoading: loadingEdit }] = useUpdateRoleMutation();
  const [assignCapabilityBulk, { isLoading: loadingBulk }] =
    useAssignCapabilityBulkMutation();

  useEffect(() => {
    if (getCapability && getCapability?.Data) {
      setCapability(getCapability?.Data);
    }
  }, [getCapability]);

  useEffect(() => {
    if (roleId && dataIdRole && dataIdRole.Data) {
      setFormData({
        id: dataIdRole.Data.id,
        name: dataIdRole.Data.name,
        enabled: true,
        capabilities: dataIdRole.Data.capabilities?.map((el) => el.id),
      });
    }
  }, [roleId, dataIdRole]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeRadio = (event) => {
    setFormData({ ...formData, enabled: event.target.value === "true" });
  };

  const handleAssignCapability = (id) => {
    // Verifica se l'ID è uno dei ruoli principali (1000, 1001, 1002)
    const isMainRole = adminRoleIds.includes(id);

    if (isMainRole) {
      // Se è un ruolo principale, rimuovi gli altri due ruoli principali
      const otherMainRoles = adminRoleIds.filter((roleId) => roleId !== id);

      // Rimuovi gli altri ruoli principali se presenti
      otherMainRoles.forEach((roleId) => {
        if (formData.capabilities?.includes(roleId)) {
          handleRevokeCapability(roleId);
        }
      });

      applyRolePreset(id);
    } else {
      const isDisabled = isCapabilityDisabled(id);
      if (!isDisabled) {
        setFormData((prevData) => ({
          ...prevData,
          capabilities: [...prevData.capabilities, id],
        }));
      }
    }
  };

  const applyRolePreset = (roleId) => {
    let presetCapabilities = [];

    switch (roleId) {
      case 1000: // ADMIN
        presetCapabilities = [...presetAdmin, roleId];
        break;
      case 1001: // STUDIO
        presetCapabilities = [...presetStudio, roleId];
        break;
      case 1002: // OPERATOR
        presetCapabilities = [...presetOperator, roleId];
        break;
      case 1003: // ADMIN GROUP
        presetCapabilities = [...presetAdminGroup, roleId];
        break;
      default:
        presetCapabilities = [roleId];
    }

    setFormData((prevData) => {
      return {
        ...prevData,
        capabilities: presetCapabilities,
      };
    });
  };

  const getSelectedMainRole = () => {
    const selectedMainRoles = adminRoleIds.filter((roleId) =>
      formData.capabilities?.includes(roleId)
    );
    return selectedMainRoles.length > 0 ? selectedMainRoles[0] : null;
  };

  const isCapabilityDisabled = (capabilityId) => {
    const selectedMainRole = getSelectedMainRole();

    if (!selectedMainRole || adminRoleIds.includes(capabilityId)) {
      return false;
    }

    let allowedCapabilities = [];
    switch (selectedMainRole) {
      case 1000: // ADMIN
        allowedCapabilities = presetAdmin;
        break;
      case 1001: // STUDIO
        allowedCapabilities = presetStudio;
        break;
      case 1002: // OPERATOR
        allowedCapabilities = presetOperator;
        break;
      case 1003: // ADMIN GROUP
        allowedCapabilities = presetAdminGroup;
        break;
      default:
        return false;
    }

    return !allowedCapabilities.includes(capabilityId);
  };

  const handleRevokeCapability = (id) => {
    const isMainRole = adminRoleIds.includes(id);

    if (isMainRole) {
      setFormData((prevData) => {
        const currentCapabilities = prevData.capabilities || [];
        let capabilitiesToRemove = [];

        switch (id) {
          case 1000: // ADMIN
            capabilitiesToRemove = [...presetAdmin, id];
            break;
          case 1001: // STUDIO
            capabilitiesToRemove = [...presetStudio, id];
            break;
          case 1002: // OPERATOR
            capabilitiesToRemove = [...presetOperator, id];
            break;
          case 1003: // ADMIN GROUP
            capabilitiesToRemove = [...presetAdminGroup, id];
            break;
          default:
            capabilitiesToRemove = [id];
        }

        const newCapabilities = currentCapabilities.filter(
          (capId) => !capabilitiesToRemove.includes(capId)
        );

        return {
          ...prevData,
          capabilities: newCapabilities,
        };
      });
    } else {
      const isDisabled = isCapabilityDisabled(id);
      if (!isDisabled) {
        setFormData((prevData) => ({
          ...prevData,
          capabilities: prevData.capabilities.filter((capId) => capId !== id),
        }));
      }
    }
  };

  const handleSave = async () => {
    try {
      const selectedCapabilities = formData.capabilities || [];

      if (roleId) {
        const bodyRole = {
          id: formData.id,
          name: formData.name,
        };

        const editResult = await editRole(bodyRole).unwrap();
        if (editResult?.Result === 1 && selectedCapabilities.length > 0) {
          await assignCapabilityBulk({
            id: roleId,
            capabilityIds: selectedCapabilities,
          }).unwrap();
        }
        toast.success(t("RolesPermitted") + " " + t("Updated"));
        onClose();
      } else {
        const bodyRole = {
          name: formData.name,
          capabilities: selectedCapabilities,
        };

        const createResult = await createRole(bodyRole).unwrap();
        if (createResult?.Result === 1) {
          toast.success(t("RolesPermitted") + " " + t("Created"));
          onClose();
        }
      }
    } catch (err) {
      toast.error(t("ReqErr"));
      console.log(err);
    }
  };

  return (
    <div className="flex flex-col justify-center items-center">
      <h2>
        {roleId
          ? t("Edit") + " " + t("RolesPermitted")
          : t("Created") + " " + t("RolesPermitted")}
      </h2>
      <div className="flex flex-col items-center w-full p-8 space-y-6">
        <div className="w-full flex space-x-8">
          <CoreInputField
            className="w-full"
            label={t("Name")}
            name="name"
            onChange={handleChange}
            value={formData?.name}
          />
          {roleId && (
            <div className="flex w-full justify-between items-center pt-4">
              <h3 className="m-0">{t("Stato ruolo")}</h3>
              <RadioGroup
                row
                value={formData.enabled}
                className="flex"
                onChange={handleChangeRadio}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio size="small" />}
                  label={t("Attivo")}
                />
                <FormControlLabel
                  value={false}
                  control={<Radio size="small" />}
                  label={t("Disattivo")}
                />
              </RadioGroup>
            </div>
          )}
        </div>

        <div className="flex space-x-8 h-full">
          <div className="">
            <h3 className="mb-4">{t("RolesPermitted")}</h3>
            <div
              className="rounded p-4 h-full"
              style={{ border: "1px solid lightgrey" }}
            >
              <div className="">
                <h4 className="text-red-600 m-2">{t("Roles")}</h4>
                <div className="flex flex-wrap gap-2">
                  {capability
                    ?.filter((el) => adminRoleIds.includes(el.id))
                    .map((el, index) => (
                      <CoreCapabilities
                        key={index}
                        elem={el}
                        isAdmin={true}
                        isChecked={formData.capabilities?.includes(el.id)}
                        onAssign={handleAssignCapability}
                        onRevoke={handleRevokeCapability}
                        setSelectedCap={setSelectedCap}
                      />
                    ))}
                </div>
              </div>

              <Divider className="my-4" />

              <div className="mt-4">
                <h4 className="text-blue-600 m-2">{t("Permessi")}</h4>
                <div className="flex flex-wrap gap-2">
                  {capability
                    ?.filter((el) => !adminRoleIds.includes(el.id))
                    .map((el, index) => {
                      const isDisabled = isCapabilityDisabled(el.id);
                      const isChecked =
                        formData.capabilities?.includes(el.id) && !isDisabled;
                      return (
                        <>
                          <CoreCapabilities
                            isDisabled={isDisabled}
                            key={index}
                            elem={el}
                            label={el?.label}
                            isAdmin={false}
                            isChecked={isChecked}
                            onAssign={handleAssignCapability}
                            onRevoke={handleRevokeCapability}
                            setSelectedCap={setSelectedCap}
                          />
                        </>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
          <div className="w-full max-h-full">
            <div className="flex items-center justify-between">
              <h3 className="mb-4">{t("Info")}</h3>
              <Button
                onClick={() => setSelectedCap(null)}
                disabled={!selectedCap}
                className={!selectedCap ? "text-gray" : "text-red-600"}
              >
                Chiudi
              </Button>
            </div>
            <div
              className="flex rounded p-4 h-full"
              style={{ border: "1px solid lightgrey" }}
            >
              <p className={`p-4 ${!selectedCap && "text-gray"}`}>
                {selectedCap || t("Nessuna Descrizione")}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-around mt-4 py-8">
        <CoreButton
          testo={
            loadingCreate || loadingEdit || loadingBulk
              ? t("Wait")
              : roleId
              ? t("Save")
              : t("Create")
          }
          className="bg-primary btn-round border-primary"
          onClick={handleSave}
          disabled={loadingCreate || loadingEdit || loadingBulk}
        />
      </div>
    </div>
  );
};

export default DetailRuoli;
